import { AfterViewInit, ChangeDetectorRef, Component, inject, model, OnInit, ViewChild } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CommonModule } from '@angular/common';
import { SearchUserRequestVo, UserManagementService, UserRequestVo } from '../user-management.service';
import { SimpleCodeValue } from '../../../core/models/code-value.model';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatDialog } from '@angular/material/dialog';
import { PopupDialogComponent } from '../../../components/popup-dialog/popup-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UserRquestPopupDialogComponent } from '../user-request-dialog/user-request-dialog.component';

@Component({
  selector: 'app-manage-user-requests',
  standalone: true,
  imports: [
    MatCardModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    MatTableModule,
    CommonModule,
    MatPaginatorModule,
    MatSortModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatMenuModule
  ],
  templateUrl: './manage-user-requests.component.html',
  styleUrls: ['./manage-user-requests.component.scss'],
})
export class ManageUserRequestsComponent implements OnInit, AfterViewInit {

  private snackBar = inject(MatSnackBar);

  isLoading = true; // Initially true to show the loader while data is being fetched
  zoneId!: number;
  requestForm = new FormGroup({
    residentName: new FormControl(''),
    mobileNumber: new FormControl(''),
    areaName: new FormControl(''),
    requestStatus: new FormControl<string[]>(['PENDING'])  
  });

  areas: SimpleCodeValue[] = [];
  requestStatuses = ['APPROVED', 'REJECTED', 'PENDING'];

  displayedColumns: string[] = [
    'residentName', 'mobileNumber', 'areaName', 'location', 'requestDate', 'requestStatus', 'view'
  ];
  dataSource = new MatTableDataSource<UserRequestVo>([]);

  @ViewChild(MatSort) set matSort(sort: MatSort) {
    if (sort) {
      this.dataSource.sort = sort;
      this.dataSource.sortingDataAccessor = (item: UserRequestVo, property: string) => {
        if (property === 'requestDate') {
          return new Date(item.requestDate).getTime();
        }
        return (item as any)[property];
      };
    }
  }

  @ViewChild(MatPaginator) set matPaginator(paginator: MatPaginator) {
    if (paginator) {
      this.dataSource.paginator = paginator;
    }
  }

  readonly event = model('');
  readonly dialog = inject(MatDialog);

  constructor(
    private userManagementService: UserManagementService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.getUserZoneAreas();
    this.onSearch();
  }

  ngAfterViewInit(): void {
    // if (this.dataSource) {
    //   this.dataSource.paginator = this.paginator;
    //   this.dataSource.sort = this.sort;
    // }
    this.cdr.detectChanges();
  }

  getUserZoneAreas() {
    this.userManagementService.getUserAreas().subscribe({
      next: (response) => {
        this.areas = response;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('POST error:', error);
      },
    });
  }

  onSearch() {
    this.isLoading = true; // Show loader before fetching data
    const { residentName, mobileNumber, areaName, requestStatus } = this.requestForm.value;
    const searchRequest: SearchUserRequestVo = {
      residentName: residentName,
      mobileNumber: mobileNumber,
      areaName: areaName,
      requestStatus: requestStatus,
      offset: 0,
      limit: 100
    };

    this.userManagementService.searchUserRequests(searchRequest).subscribe({
      next: (response: UserRequestVo[]) => {
        console.log('done');
        this.dataSource.data = response;
        this.isLoading = false; // Hide loader once data is fetched
      },
      error: (error) => {
        console.error('POST error:', error);
        this.isLoading = false; // Hide loader once data is fetched
      }
    });
  }

  onReset() {
    this.requestForm.reset({});
    this.onSearch(); // Reload data after resetting form
  }

  onApprove(data: UserRequestVo) {
    const dialogRef = this.dialog.open(UserRquestPopupDialogComponent, {
      data: { 
        event: data,
        message: 'Are you sure you want to approve the request?',
        title: data.residentName + ' - ' + data.requestStatus,
        type: 'APPROVAL',
        areas: this.areas
      },
      width: '80%',
      height: '100%',
      maxWidth: '100%',
      maxHeight: '100%',
      panelClass: 'full-screen-dialog'
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log(result);
      if (result.approved) {
        this.userManagementService.approveUserRequest(result.location.allocationId, data.userId, result.location.locationId).subscribe({
          next: (response) => {
            if(response.valid) {
              this.openSnackBar(response.message, 'Okay');
              this.onSearch();
            } else {
              this.openSnackBar(response.message, 'Okay');
            }
          },
          error: (error) => {
            console.error('POST error:', error);
            this.openSnackBar('Something went wrong', 'Okay');
          }
        });
      } else {
        // Clicked NO
      }
    });

  }

  onReject(data: UserRequestVo) {
    const dialogRef = this.dialog.open(PopupDialogComponent, {
      data: { event: data, message: 'Are you sure you want to reject the request?', title: data.residentName + ' - ' + data.requestStatus },
      width: '500px',
      height: '200px'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result !== undefined) {
        data.requestStatus = 'REJECTED';
        this.userManagementService.updateUserRequest(data).subscribe({
          next: (response) => {
            if(response.valid) {
              this.openSnackBar(response.message, 'Okay');
              this.onSearch();
            } else {
              this.openSnackBar(response.message, 'Okay');
            }
          },
          error: (error) => {
            console.error('POST error:', error);
          }
        });
      } else {
        // Clicked NO
      }
    });
  }

  onView(data: UserRequestVo) {
    const dialogRef = this.dialog.open(UserRquestPopupDialogComponent, {
      data: { 
        event: data,
        message: 'Are you sure you want to approve the request?',
        title: data.residentName + ' - ' + data.requestStatus,
        type: 'VIEW',
        areas: this.areas
      },
      width: '65%',
      height: '70%',
      maxHeight: '100%',
      maxWidth: '100%',
      panelClass: 'full-screen-dialog'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result !== undefined) {
        // alert('yes');
      } else {
        // Clicked NO
      }
    });
  }

  openSnackBar(message: string, action?: string) {
    this.snackBar.open(message, action, { duration: 3 * 1000 }); // 3 seconds
  }

}
